// P32 — Page Admin Import/Export référentiels (refonte).
// Permet à l'admin d'uploader ses propres fichiers CSV (au lieu de dépendre
// de scripts hardcodés) et d'exporter la base en CSV.
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { useT } from "@/lib/i18n";
import { useCan } from "@/hooks/useCan";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Upload, Download, FileText, Loader2, CheckCircle2, XCircle,
  AlertTriangle, History, FileUp,
} from "lucide-react";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";
interface EntityConfig {
  id: string;
  label: string;
  columns: string[];
}

interface ImportHistoryItem {
  id: number;
  entity: string;
  mode: string;
  user_email: string;
  total_rows: number;
  created: number;
  updated: number;
  duration_ms: number;
  created_at: string;
}

type Tab = "import" | "export" | "history";

export default function ImportPage() {
  const { toast } = useToast();
  const t = useT();
  const canWrite = useCan("write"); // respo+ only

  const [tab, setTab] = useState<Tab>("import");
  const [entities, setEntities] = useState<EntityConfig[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<any | null>(null);
  const [showCommitConfirm, setShowCommitConfirm] = useState(false);
  const [history, setHistory] = useState<ImportHistoryItem[]>([]);

  // Charge la liste des entités au mount
  useState(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/v1/admin/import/entities`, {
          credentials: "include",
        });
        if (r.ok) {
          const data = await r.json();
          setEntities(data.entities);
          if (data.entities.length > 0) {
            setSelectedEntity(data.entities[0].id);
          }
        }
      } catch {
        // silencieux
      }
    })();
  });

  // Charge l'historique
  const loadHistory = async () => {
    try {
      const r = await fetch(`${API_BASE}/v1/admin/import/history?limit=50`, {
        credentials: "include",
      });
      if (r.ok) {
        const data = await r.json();
        setHistory(data.history);
      }
    } catch {
      // silencieux
    }
  };

  // Charge l'historique quand l'onglet est ouvert
  if (tab === "history" && history.length === 0) {
    loadHistory();
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setDryRunResult(null);
  };

  const runDryRun = async () => {
    if (!file || !selectedEntity) return;
    setImporting(true);
    setDryRunResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", "dry-run");
      const r = await fetch(
        `${API_BASE}/v1/admin/import/${selectedEntity}`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `API ${r.status}`);
      setDryRunResult(data);
      if (data.errors && data.errors.length > 0) {
        toast({
          title: t("import.errors_detected"),
          description: `${data.errors.length} erreur(s) dans le CSV`,
          variant: "warning",
        });
      }
    } catch (err) {
      toast({
        title: t("import.dry_run_error"),
        description: (err as Error).message,
        variant: "error",
      });
    } finally {
      setImporting(false);
    }
  };

  const runCommit = async () => {
    if (!file || !selectedEntity) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", "commit");
      const r = await fetch(
        `${API_BASE}/v1/admin/import/${selectedEntity}`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `API ${r.status}`);
      toast({
        title: t("import.success"),
        description: `${data.created} créés, ${data.updated} mis à jour en ${data.duration_ms}ms`,
        variant: "success",
      });
      setFile(null);
      setDryRunResult(null);
      loadHistory();
    } catch (err) {
      toast({
        title: t("import.error"),
        description: (err as Error).message,
        variant: "error",
      });
    } finally {
      setImporting(false);
      setShowCommitConfirm(false);
    }
  };

  const downloadTemplate = async () => {
    if (!selectedEntity) return;
    try {
      const r = await fetch(
        `${API_BASE}/v1/admin/import/template/${selectedEntity}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`API ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `template-${selectedEntity}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: t("import.download_error"),
        description: (err as Error).message,
        variant: "error",
      });
    }
  };

  const downloadExport = async (format: "csv" | "json") => {
    if (!selectedEntity) return;
    try {
      const r = await fetch(
        `${API_BASE}/v1/admin/export/${selectedEntity}?format=${format}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`API ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedEntity}-${new Date().toISOString().split("T")[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: t("import.export_error"),
        description: (err as Error).message,
        variant: "error",
      });
    }
  };

  if (!canWrite) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-amber-500" />
            <p>{ht("Vous n'avez pas les droits pour accéder à cette page.")}</p>
            <p className="text-xs">{ht("Réservée au rôle Responsable ou Admin système.")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedEntityConfig = entities.find((e) => e.id === selectedEntity);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{ht("Import / Export")}</h1>
        <p className="text-muted-foreground">
          {t("import.subtitle_csv")}
        </p>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setTab("import")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "import"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Upload className="h-4 w-4" />
          Importer
        </button>
        <button
          onClick={() => setTab("export")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "export"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Download className="h-4 w-4" />
          Exporter
        </button>
        <button
          onClick={() => setTab("history")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "history"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <History className="h-4 w-4" />
          {t("import.tab_history")}
        </button>
      </div>

      {/* Onglet Import */}
      {tab === "import" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{ht("Importer un fichier CSV")}</CardTitle>
              <CardDescription>
                Uploadez un fichier CSV pour ajouter ou mettre à jour des données.
                Le mode dry-run vérifie sans modifier.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">{ht("Type de données")}</label>
                <select
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedEntity}
                  onChange={(e) => {
                    setSelectedEntity(e.target.value);
                    setFile(null);
                    setDryRunResult(null);
                  }}
                >
                  {entities.map((e) => (
                    <option key={e.id} value={e.id}>
                      {t(`import.entity.${e.id}`)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedEntityConfig && (
                <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
                  <strong>{ht("Colonnes attendues :")}</strong>{" "}
                  {selectedEntityConfig.columns.join(", ")}
                </div>
              )}

              <div>
                <label className="text-sm font-medium">{ht("Fichier CSV")}</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    disabled={!selectedEntity}
                  >
                    <FileText className="mr-1 h-4 w-4" />
                    Template
                  </Button>
                </div>
                {file && (
                  <p className="mt-1 text-xs text-muted-foreground">{ht("Sélectionné : ")}<strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={runDryRun}
                  disabled={!file || importing}
                  variant="outline"
                >
                  {importing ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <FileUp className="mr-1 h-4 w-4" />
                  )}
                  Vérifier (dry-run)
                </Button>
                <Button
                  onClick={() => setShowCommitConfirm(true)}
                  disabled={!file || !dryRunResult}
                >
                  <Upload className="mr-1 h-4 w-4" />
                  {t("import.apply_title").replace(" ?", "")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Résultat du dry-run */}
          {dryRunResult && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {dryRunResult.errors?.length > 0 ? (
                    <XCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  )}
                  Résultat de la vérification
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground">{ht("Lignes totales")}</div>
                    <div className="text-2xl font-bold">{dryRunResult.total_rows}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">{ht("À créer")}</div>
                    <div className="text-2xl font-bold text-green-600">
                      +{dryRunResult.created}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">{ht("À mettre à jour")}</div>
                    <div className="text-2xl font-bold text-blue-600">
                      ~{dryRunResult.updated}
                    </div>
                  </div>
                </div>
                {dryRunResult.errors && dryRunResult.errors.length > 0 && (
                  <div className="mt-4">
                    <div className="mb-1 text-sm font-semibold text-red-600">
                      {dryRunResult.errors.length} erreur(s) :
                    </div>
                    <ul className="space-y-1 text-xs">
                      {dryRunResult.errors.slice(0, 5).map((e: any, i: number) => (
                        <li key={i} className="rounded bg-red-50 dark:bg-red-950/30 p-2 text-red-700 dark:text-red-300">
                          Ligne {e.row}: {e.error}
                        </li>
                      ))}
                      {dryRunResult.errors.length > 5 && (
                        <li className="text-xs text-muted-foreground">
                          ... et {dryRunResult.errors.length - 5} autres
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                <p className="mt-3 text-xs text-muted-foreground">{ht("Mode : ")}<strong>{dryRunResult.mode}</strong> · Durée : {dryRunResult.duration_ms}ms
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Onglet Export */}
      {tab === "export" && (
        <Card>
          <CardHeader>
            <CardTitle>{ht("Exporter les données")}</CardTitle>
            <CardDescription>
              Téléchargez toutes les données d'une entité en CSV ou JSON.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">{ht("Type de données")}</label>
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
              >
                {entities.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => downloadExport("csv")}
                disabled={!selectedEntity}
                variant="outline"
              >
                <Download className="mr-1 h-4 w-4" />
                Télécharger CSV
              </Button>
              <Button
                onClick={() => downloadExport("json")}
                disabled={!selectedEntity}
                variant="outline"
              >
                <Download className="mr-1 h-4 w-4" />
                Télécharger JSON
              </Button>
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
              <strong>{ht("Format du fichier :")}</strong> CSV avec headers en première ligne,
              séparateur virgule, encoding UTF-8. Les IDs sont exportés, pas les noms.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Onglet Historique */}
      {tab === "history" && (
        <Card>
          <CardHeader>
            <CardTitle>{ht("Historique des imports")}</CardTitle>
            <CardDescription>
              {history.length} import(s) récent(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t("import.empty_state")}
              </div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="px-2 py-2">{ht("Date")}</th>
                    <th className="px-2 py-2">{ht("Entité")}</th>
                    <th className="px-2 py-2">{ht("Mode")}</th>
                    <th className="px-2 py-2 text-right">{ht("Lignes")}</th>
                    <th className="px-2 py-2 text-right">{ht("Créés")}</th>
                    <th className="px-2 py-2 text-right">{ht("MAJ")}</th>
                    <th className="px-2 py-2 text-right">{ht("Durée")}</th>
                    <th className="px-2 py-2">{ht("Par")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b">
                      <td className="px-2 py-2 text-xs">
                        {new Date(h.created_at).toLocaleString("fr-FR")}
                      </td>
                      <td className="px-2 py-2 font-semibold">{h.entity}</td>
                      <td className="px-2 py-2">
                        <span
                          className={
                            h.mode === "commit"
                              ? "rounded bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300"
                              : "rounded bg-amber-500/15 border border-amber-500/40 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300"
                          }
                        >
                          {h.mode}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right font-mono">{h.total_rows}</td>
                      <td className="px-2 py-2 text-right font-mono text-green-600">
                        +{h.created}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-blue-600">
                        ~{h.updated}
                      </td>
                      <td className="px-2 py-2 text-right text-xs text-muted-foreground">
                        {h.duration_ms}ms
                      </td>
                      <td className="px-2 py-2 text-xs">{h.user_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={showCommitConfirm}
        title={t("import.apply_title")}
        description={t("import.apply_description")}
        confirmLabel={t("import.apply_confirm")}
        variant="destructive"
        loading={importing}
        onConfirm={runCommit}
        onCancel={() => setShowCommitConfirm(false)}
      />
    </div>
  );
}
